import { CommonModule } from '@angular/common';
import { Select, Store } from '@ngxs/store';

import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, OnInit, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from '../../../../../shared/services/sidenav.service';
import { SharedModule } from '../../../../../shared/shared.module';
import { GetProjects, ProjectsState } from '../../project.state';
import { combineLatest, Observable } from 'rxjs';
import { Pagination } from '../../project.state';
import {
    DisplayedColumns,
    DisplayType,
} from '../../../../../shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import {
    AlertService,
    ButtonColors,
    ButtonModule,
    DropdownMenuItem,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { BusinessActivityModel, ProjectModel, ProjectStatus } from '../../project.models';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderService } from 'src/app/shared/services/header.service';
import { User } from 'src/app/core/models/user.model';
import { EDSearchComponent } from 'src/app/shared/components/ed-search/ed-search.component';
import { ProjectsPageSize } from 'src/app/shared/all.constants';
import { ProjectsService } from '../../project.service';

export enum ProjectOutputEvent {
    ACCESS = 'ACCESS',
}

@UntilDestroy()
@Component({
    selector: 'app-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        ReactiveFormsModule,
        FormsModule,
        ButtonModule,
        TextfieldModule,
        EDSearchComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProjectsComponent {
    ButtonColors = ButtonColors;

    @Select(ProjectsState.activities)
    public activities$!: Observable<BusinessActivityModel[]>;
    public activityList: { value: string; label: string }[] = [];
    public activityFilters: string[] = [];

    public statusList = Object.values(ProjectStatus).map((value) => ({ value, label: value }));
    public statusFilters: ProjectStatus[] = [];

    public q: string = '';

    public sort: { key: string; direction: number } = { key: 'code', direction: -1 };

    @Select(ProjectsState.leadUsers)
    public leadUsers$!: Observable<User[]>;
    public leadUserList: { value: string; label: string }[] = [];
    public leadUsersFilters: string[] = [];

    @Select(ProjectsState.allProjects)
    public projects$!: Observable<ProjectModel[]>;
    public projectsDataSource: MatTableDataSource<{}> = new MatTableDataSource<{}>();

    @Select(ProjectsState.totalCount)
    public totalCount$!: Observable<number>;
    public totalCount: number = 0;

    @Select(ProjectsState.pagination)
    public pagination$!: Observable<Pagination>;
    public pagination: Pagination = {};

    @Select(ProjectsState.isLoading)
    public isLoading$!: Observable<boolean>;
    public isLoading: boolean = false;

    public projectsData = new MatTableDataSource<{}>([]);

    public projectsColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code',
            objectKey: 'code',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Label',
            objectKey: 'label',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Activité',
            objectKey: 'activityLabel',
            disableSort: true,
            bold: false,
        },

        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant total',
            objectKey: 'soldAmount',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Sous traitance',
            objectKey: 'subcontractingAmount',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant facturé',
            objectKey: 'invoicedAmount',
            hide: true,
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant net',
            objectKey: 'netAmount',
            hide: true,
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Reste à facturer',
            objectKey: 'remainingToInvoiceAmount',
            disableSort: true,
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Jours théoriques',
            objectKey: 'daysSold',
            hide: true,
            disableSort: true,
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Jours passés',
            objectKey: 'daysDeclared',
            disableSort: true,
            hide: true,
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Conso factu (%)',
            objectKey: 'billingsRate',
            disableSort: true,
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Conso Temps (%)',
            objectKey: 'daysRate',
            disableSort: true,
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Chef.fe de projet',
            objectKey: 'leadUserName',
            disableSort: true,
            bold: false,
        },

        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Reste budget temps passé',
            objectKey: 'remainingBudget',
            disableSort: true,
            bold: false,
            hide: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Etat',
            objectKey: 'status',

            bold: false,
        },
    ];

    constructor(
        private router: Router,
        private sidenavService: SidenavService,
        private headerService: HeaderService,
        public store: Store,
        public alertService: AlertService,
        public projectsService: ProjectsService,
    ) {
        this.headerService.setBreadcrumbs([{ label: 'Projets' }]);

        this.sidenavService.resetSubMenuItem('Projets', [
            {
                routerLink: ['/main/projects'],
                label: 'Tous les projets',
                isActive: true,
                icon: 'bi bi-folder2-open',
                subItems: [],
            },
        ]);

        this.activities$
            .pipe(untilDestroyed(this))
            .subscribe(
                (activities) =>
                    (this.activityList = activities.map((a) => ({ value: a.id, label: a.label }))),
            );
        this.leadUsers$.pipe(untilDestroyed(this)).subscribe(
            (leadUserList) =>
                (this.leadUserList = leadUserList.map((l) => ({
                    value: l.id,
                    label: `${l.firstname} ${l.lastname}`,
                }))),
        );
        this.projects$.pipe(untilDestroyed(this)).subscribe((projects: ProjectModel[]) => {
            this.projectsDataSource = new MatTableDataSource<{}>(
                this.generateDataProjects(projects),
            );
        });

        this.isLoading$
            .pipe(untilDestroyed(this))
            .subscribe((isLoading: boolean) => (this.isLoading = isLoading));

        combineLatest([this.totalCount$, this.pagination$])
            .pipe(untilDestroyed(this))
            .subscribe(([totalCount, pagination]) => {
                this.totalCount = totalCount;
                this.pagination = pagination;
            });
    }

    generateDataProjects(projects: ProjectModel[]) {
        return projects.map((project: ProjectModel) => {
            return {
                ...project,
                activityLabel: project.activity?.label,
                billingsRate: project.soldAmount
                    ? Math.floor((100 * (project.invoicedAmount ?? 0)) / project.soldAmount)
                    : 0,
                netAmount: (project.soldAmount ?? 0) - (project.subcontractingAmount ?? 0),
                daysRate: project.daysSold
                    ? Math.floor((100 * (project.daysDeclared ?? 0)) / project.daysSold)
                    : 0,
                leadUserName: project.leadUser?.lastname,
                remainingBudget:
                    (project.soldAmount ?? 0) -
                    (project.subcontractingAmount ?? 0) -
                    project.daysDeclared * 750,
                actions: this.getActions(),
            };
        });
    }

    updateActivityFilters(ids: any): void {
        this.activityFilters = ids;
        this.updateTable(true);
    }

    updateLeadUsersFilters(ids: any): void {
        this.leadUsersFilters = ids;
        this.updateTable(true);
    }

    updateStatusFilters(ids: any): void {
        this.statusFilters = ids;
        this.updateTable(true);
    }

    updateQuerySearch(q: string) {
        this.q = q;
        this.updateTable(true);
    }

    updateTable(reset: boolean = false) {
        const pagination: Pagination = {
            ...this.pagination,
            q: this.q !== '' ? this.q : null,
            activityId: this.activityFilters,
            status: this.statusFilters,
            leadUser: this.leadUsersFilters,
            offset: reset ? 0 : this.projectsDataSource.data.length,
            limit: this.pagination.limit || ProjectsPageSize,
            sort: this.sort,
            reset,
        };
        return this.store.dispatch(new GetProjects({ pagination }));
    }

    getActions(): DropdownMenuItem[] {
        return [
            {
                icon: 'icon-eye',
                text: 'Accéder',
                outputEventString: ProjectOutputEvent.ACCESS,
            },
        ];
    }

    async exportAll() {
        const pagination: Pagination = {
            offset: 0,
            limit: this.totalCount,
        };
        const { total, items } = await this.projectsService.getProjects(pagination);
        const data = this.generateDataProjects(items);
        // 1. Construire les lignes
        const csvRows = [
            this.projectsColumns.map((d) => d.objectDisplayName).join(';'), // première ligne : en-têtes séparés par ";"
            ...data.map((row: any) =>
                this.projectsColumns
                    .map((d) => d.objectKey)
                    .map((field) => {
                        let val = row[field] ?? '';
                        // Escape des guillemets et remplacement des retours à la ligne
                        val = String(val).replace(/"/g, '""').replace(/\n/g, ' ');
                        return `"${val}"`;
                    })
                    .join(';'),
            ),
        ];

        // 2. Fusionner en une seule string
        const csvContent = csvRows.join('\n');

        // 3. Créer un blob et déclencher le téléchargement
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        const filename = `${new Date().toLocaleDateString()}_projets.csv`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Libérer l’URL
        window.URL.revokeObjectURL(url);
    }
    goToDetail(projectId: any) {
        const projet: any = this.projectsDataSource.data.find((p: any) => p.id == projectId);
        if (projet && projet.code) {
            this.router.navigate([`/main/projects/${projet?.code}/general`]);
        }
    }

    displayMoreProjects() {
        this.updateTable();
    }

    onSortChanged(event: any) {
        const key = event.active;
        const direction = event._direction === 'asc' ? 1 : -1;

        if (this.sort.key !== key || this.sort.direction !== direction) {
            this.sort = { key, direction };
            this.updateTable(true);
        }
    }

    onDropdownMenuClick(projectId: any, event: string): void {
        switch (event) {
            case ProjectOutputEvent.ACCESS:
                this.goToDetail(projectId);
                break;
            default:
                break;
        }
    }

    public openModalCreate(): void {
        this.router.navigate([`/main/projects/new`]);
    }
}
